import { type ReactNode, useEffect, useMemo, useState } from "react";

import {
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerHeader,
} from "@heroui/drawer";
import { Button, Spinner } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useToggle } from "usehooks-ts";

import { useLanguage } from "@onesolution/hooks";

export enum OpenMode {
	creation = "creation",
	edition = "edition",
	details = "details",
	close = 0,
}

type DrawerHeaderFunc<T> = (
	selectedItem?: T,
) => string | null | undefined | ReactNode;
type DrawerBodyFunc<T> = (selectedItem?: T) => ReactNode;

export function FormDrawer<T>({
	openMode: baseOpenMode,
	onClose,
	selectedItem,
	form,
	headers,
	bodies,
	onEdit,
	canEdit = true,
	isLoading = false,
	closeOnClickOutside = true,
}: {
	openMode: OpenMode;
	onClose: () => void;
	selectedItem?: T;
	canEdit?: boolean;
	onEdit?: () => void;
	form: (props: {
		mode: OpenMode;
		onClose: () => void;
		loadedData?: T;
	}) => ReactNode;
	isLoading?: boolean;
	closeOnClickOutside?: boolean;
	headers: {
		[OpenMode.creation]: DrawerHeaderFunc<T>;
		[OpenMode.details]: DrawerHeaderFunc<T>;
		[OpenMode.edition]: DrawerHeaderFunc<T>;
	};
	bodies?: {
		[OpenMode.creation]?: DrawerBodyFunc<T>;
		[OpenMode.details]?: DrawerBodyFunc<T>;
		[OpenMode.edition]?: DrawerBodyFunc<T>;
		[OpenMode.close]?: DrawerBodyFunc<T>;
	};
}) {
	const [openMode, setOpenMode] = useState<OpenMode>(baseOpenMode);
	const { t } = useLanguage();
	useEffect(() => setOpenMode(baseOpenMode), [baseOpenMode]);

	const isOpen = useMemo(() => {
		if (openMode === OpenMode.close) return false;
		if (isLoading) return true;
		if (openMode === OpenMode.creation) return true;
		if (selectedItem) return true;

		return false;
	}, [openMode, selectedItem, isLoading]);

	const [isFullScreen, toggleFullScreen] = useToggle(false);

	const body = useMemo(() => {
		if (isLoading && !selectedItem) return <Spinner />;
		if (bodies?.[openMode]) return bodies[openMode]();

		return form({ mode: openMode, onClose, loadedData: selectedItem });
	}, [isLoading, bodies, onClose, form, openMode, selectedItem]);

	return (
		<Drawer
			hideCloseButton
			isOpen={isOpen}
			onOpenChange={closeOnClickOutside ? onClose : undefined}
			backdrop="transparent"
			size={isFullScreen ? "full" : "4xl"}
		>
			<DrawerContent
				className={`
			bg-background/70 text-foreground p-6 backdrop-blur-xl transition-all duration-300
			${isFullScreen ? "ml-72 w-[calc(100%-20rem)]" : ""}
		`}
			>
				{() => (
					<>
						<DrawerHeader className="text-lg py-6 font-bold flex items-center z-20">
							<Icon
								className="cursor-pointer"
								icon="solar:arrow-left-outline"
								width={24}
								height={24}
								onClick={onClose}
							/>
							{openMode !== OpenMode.close && (
								<h2 className="flex justify-center w-full">
									{isLoading ? null : headers[openMode](selectedItem)}
								</h2>
							)}
							{openMode === OpenMode.details && !isLoading && canEdit && (
								<Button
									variant="flat"
									color="primary"
									startContent={
										<Icon
											icon="solar:pen-new-square-linear"
											width={80}
											height={80}
										/>
									}
									onPress={() => {
										setOpenMode(OpenMode.edition);
										onEdit?.();
									}}
								>
									{t("basic.edit")}
								</Button>
							)}
							<Icon
								className="cursor-pointer ml-4"
								icon={
									isFullScreen ? "solar:minimize-bold" : "solar:maximize-bold"
								}
								width={24}
								height={24}
								onClick={toggleFullScreen}
							/>
						</DrawerHeader>
						<DrawerBody className="flex items-center">{body}</DrawerBody>
					</>
				)}
			</DrawerContent>
		</Drawer>
	);
}
